import { TEMPLATES, describeStep, type Binding, type StepTimingSpec, type MotionPath, type MotionPlanInput, type MotionSlot, type MotionStepInput } from '../animation/procedural/spec';
import { parsePlan } from '../animation/procedural/compiler';
import { OpError } from '../domain/projectOps';
import { applyMotionPlan, type ConflictPolicy } from '../domain/motionOps';
import * as S from '../domain/sceneOps';
import type { Project, Scene, SceneObject } from '../domain/schemas';
import type { AnimationSelection } from './animationCommands';
import type { Command } from './commands';

type StepCommand = Extract<Command, { type: 'motion.step' }>;

/** A plan waiting in the Motion Composer (UI state, never saved in the project). */
export interface MotionDraft {
  plan: MotionPlanInput;
  binding: Binding;
  policy: ConflictPolicy;
  allowExtend: boolean;
  /** The animation it was planned for (null: the animation selected when applying). */
  animationId: string | null;
}

/** What the executor needs from the Motion Composer. */
export interface MotionContext {
  /** The composer's current plan (for "apply the motion plan"). */
  draft(): MotionDraft | null;
  /** The path drawn in the composer (for "follow this path"). */
  path(): MotionPath | null;
}

const slug = (o: SceneObject, taken: Set<string>) => {
  const base = `${o.type === 'character' ? 'char' : 'obj'}-${o.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 20) || 'x'}`;
  let name = base.match(/^[a-z]/) ? base : `t-${base}`;
  for (let i = 2; taken.has(name); i++) name = `${base}-${i}`;
  taken.add(name);
  return name;
};

/** Finds objects of the scene by name: exact (case-insensitive), else containing every word. */
export function objectsNamed(scene: Scene, name: string): SceneObject[] {
  const wanted = name.toLowerCase().trim();
  const exact = scene.objects.filter((o) => o.name.toLowerCase() === wanted);
  if (exact.length) return exact;
  const words = wanted.split(/\s+/);
  return scene.objects.filter((o) => {
    const n = o.name.toLowerCase();
    return words.every((w) => new RegExp(`\\b${w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`).test(n));
  });
}

function resolve(scene: Scene, target: StepCommand['target'], selection: AnimationSelection | undefined, previous: SceneObject[] | null): SceneObject[] {
  const selected = scene.objects.filter((o) => selection?.objectIds?.includes(o.id));
  const characters = (list: SceneObject[]) => list.filter((o) => o.type === 'character');
  switch (target.kind) {
    case 'inherit':
      if (previous) return previous;
      if (selected.length) return selected;
      return resolve(scene, { kind: 'character' }, selection, null);
    case 'selected':
      if (!selected.length) throw new OpError('No object is selected. Select one on the canvas, or name it (“make the neon sign pulse”).');
      return selected;
    case 'selected-character':
      if (!characters(selected).length) throw new OpError('No character is selected. Select a character instance on the canvas first.');
      return characters(selected);
    case 'character': {
      if (characters(selected).length) return characters(selected);
      const all = characters(scene.objects);
      if (all.length === 1) return all;
      if (all.length === 0) throw new OpError(`There is no character in “${scene.name}”. Place one from the character studio first.`);
      throw new OpError(`“${scene.name}” has ${all.length} characters (${all.map((o) => o.name).join(', ')}). Select the one to animate.`);
    }
    case 'name': {
      const found = objectsNamed(scene, target.name);
      if (found.length === 1) return found;
      if (found.length === 0) throw new OpError(`No object in “${scene.name}” is called “${target.name}”. Use its name from the layer list, or select it and say “the selected object”.`);
      throw new OpError(
        `“${target.name}” matches ${found.length} objects (${found.slice(0, 5).map((o) => o.name).join(', ')}${found.length > 5 ? ', …' : ''}). Use the full name or select one and say “the selected object”.`,
      );
    }
  }
}

/**
 * Turns the choreography steps of one prompt into a motion plan bound to concrete objects.
 * Timing: steps on the same target follow each other; a step on other targets with no
 * timing words starts at the beginning (parallel choreography); "at the same time" starts
 * with the previous step; "afterwards" waits for it; "at frame N"/"at N seconds" is explicit.
 * "repeat the X …"/"start the X …" amend the last step of that motion in the same prompt.
 */
export function buildPromptPlan(project: Project, commands: StepCommand[], selection: AnimationSelection | undefined, path: MotionPath | null, fps: number): { plan: MotionPlanInput; binding: Binding; replace: boolean; lines: string[] } {
  const scene = S.activeSceneOf(project);
  const slots: MotionSlot[] = [];
  const binding: Binding = {};
  const slotOf = new Map<string, string>();
  const taken = new Set<string>();
  const steps: (MotionStepInput & { objects: SceneObject[] })[] = [];
  let previous: SceneObject[] | null = null;
  let total: number | null = null;
  let replace = false;
  for (const c of commands) {
    replace ||= !!c.replace;
    if (c.planSeconds) total = c.planSeconds;
    const timing: Partial<StepTimingSpec> & Pick<StepTimingSpec, 'mode'> | null = c.timing
      ? c.timing.mode === 'at'
        ? { mode: 'at' as const, at: c.timing.atFrame !== null ? c.timing.atFrame / fps : (c.timing.at ?? 0), delay: c.timing.delay }
        : { mode: c.timing.mode, delay: c.timing.delay }
      : null;
    const duration = c.durationFrames !== undefined ? c.durationFrames / fps : c.duration;
    if (c.amend) {
      const earlier = [...steps].reverse().find((s) => s.template === c.template);
      if (earlier) {
        if (c.repeat !== undefined) earlier.repeat = c.repeat;
        if (timing) earlier.timing = timing;
        if (duration !== undefined) earlier.duration = duration;
        Object.assign(earlier.params!, c.params);
        continue;
      }
    }
    const objects = resolve(scene, c.target, selection, previous);
    for (const o of objects) {
      if (slotOf.has(o.id)) continue;
      const name = slug(o, taken);
      slotOf.set(o.id, name);
      slots.push({ name, kind: o.type === 'character' ? 'character' : 'object', label: o.name });
      binding[name] = o.id;
    }
    const targets = objects.map((o) => slotOf.get(o.id)!);
    let stepTiming: Partial<StepTimingSpec> & Pick<StepTimingSpec, 'mode'>;
    if (timing) stepTiming = { ...timing };
    else {
      // Same lane: after the last step sharing a target; otherwise parallel from the start.
      const lane = [...steps].reverse().find((s) => s.objects.some((o) => objects.includes(o)));
      stepTiming = lane ? { mode: 'after', ref: lane.id, delay: 0 } : steps.length ? { mode: 'at', at: 0, delay: 0 } : { mode: 'sequence', delay: 0 };
    }
    if (stepTiming.mode === 'with' && steps.length) stepTiming.ref = steps[steps.length - 1]!.id;
    const step: MotionStepInput & { objects: SceneObject[] } = {
      id: `p${steps.length + 1}`,
      template: c.template,
      targets,
      timing: stepTiming,
      params: { ...c.params },
      objects,
      ...(duration !== undefined ? { duration } : {}),
      ...(c.repeat !== undefined ? { repeat: c.repeat } : {}),
    };
    if (c.template === 'path') {
      if (!path) throw new OpError('There is no path yet. Draw one in the Motion Composer (path points), then say “follow this path”.');
      step.path = path;
    }
    if (TEMPLATES[c.template].characterOnly) {
      const wrong = objects.find((o) => o.type !== 'character');
      if (wrong) throw new OpError(`${TEMPLATES[c.template].label} poses a character's limbs; “${wrong.name}” is a ${wrong.type}.`);
    }
    steps.push(step);
    previous = objects;
  }
  const plan: MotionPlanInput = { version: 1, slots, steps: steps.map(({ objects: _objects, ...s }) => s), ...(total ? { totalSeconds: total } : {}) };
  const parsed = parsePlan(plan);
  const label = (slot: string) => slots.find((s) => s.name === slot)?.label ?? slot;
  return { plan, binding, replace, lines: parsed.steps.map((s, i) => `${i + 1}. ${describeStep(s, label)}`) };
}

export interface MotionCommandOutcome {
  project: Project;
  message: string;
  /** A multi-step plan to review in the Motion Composer (nothing applied yet). */
  draft?: MotionDraft;
  /** The composer's draft was applied (the composer clears it). */
  appliedDraft?: boolean;
}

function currentAnimation(project: Project, selection: AnimationSelection | undefined) {
  if (!selection?.animationId) throw new OpError('No animation is selected. Say “create a 6-second animation” first, or pick one on the timeline.');
  const a = project.animations.find((x) => x.id === selection.animationId);
  if (!a) throw new OpError('The selected animation no longer exists.');
  if (a.sceneId !== project.activeSceneId) throw new OpError(`“${a.name}” belongs to another scene. Switch scenes or create an animation here.`);
  return a;
}

/**
 * Executes the choreography of one prompt. One step is applied at once (merge policy:
 * existing keys are never overwritten unless the prompt says "replacing existing keys").
 * Several steps — or a plan created with an animation — become a draft for the Motion
 * Composer: the interpreted plan is reported and nothing is committed until the user
 * applies it ("apply the motion plan" or the Apply button).
 */
export function runMotionSteps(project: Project, commands: StepCommand[], selection: AnimationSelection | undefined, motion: MotionContext | undefined): MotionCommandOutcome {
  const a = currentAnimation(project, selection);
  const built = buildPromptPlan(project, commands, selection, motion?.path() ?? null, a.fps);
  const policy: ConflictPolicy = built.replace ? 'replace-range' : 'merge';
  const single = built.plan.steps.length === 1 && !commands.some((c) => c.planSeconds);
  if (single) {
    const r = applyMotionPlan(project, { plan: built.plan, binding: built.binding, policy, sceneId: a.sceneId, animationId: a.id });
    const warn = r.compiled.warnings.map((w) => ` ⚠ ${w.message}`).join('');
    return { project: r.project, message: `${built.lines[0]!.replace(/^1\. /, '')}: ${r.summary}${warn}` };
  }
  let check = '';
  try {
    applyMotionPlan(project, { plan: built.plan, binding: built.binding, policy, sceneId: a.sceneId, animationId: a.id });
  } catch (e) {
    check = ` ⚠ As planned it cannot be applied yet: ${e instanceof Error ? e.message : String(e)}`;
  }
  return {
    project,
    message: `Interpreted plan for “${a.name}” (not applied yet): ${built.lines.join(' ')} Review it in the Motion Composer, then press Apply or say “apply the motion plan”.${check}`,
    draft: { plan: built.plan, binding: built.binding, policy, allowExtend: false, animationId: a.id },
  };
}

/** "apply the motion plan": commits the composer's draft. */
export function applyDraft(project: Project, selection: AnimationSelection | undefined, motion: MotionContext | undefined): MotionCommandOutcome {
  const draft = motion?.draft();
  if (!draft) throw new OpError('There is no motion plan to apply. Build one in the Motion Composer or describe a sequence (“make the character run, then jump”).');
  const animationId = draft.policy === 'new-animation' ? (draft.animationId ?? selection?.animationId ?? null) : (draft.animationId ?? currentAnimation(project, selection).id);
  const r = applyMotionPlan(project, { ...draft, sceneId: project.activeSceneId, animationId });
  return { project: r.project, message: r.summary, appliedDraft: true };
}
